import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, User, BriefcaseIcon, Building, ShieldIcon, UserCircle, Loader2 } from "lucide-react";
import { Link, useLocation } from "wouter";
import { UserRole } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { AdminLayout } from "@/components/layout/AdminLayout";
import ManagerLayout from "@/components/layout/ManagerLayout";
import OwnerLayout from "@/components/layout/OwnerLayout";
import AgentLayout from "@/components/layout/AgentLayout";
import GuestLayout from "@/components/layout/GuestLayout";

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [_, navigate] = useLocation();
  const { toast } = useToast();
  
  // Profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || "",
    phone: user?.phone || ""
  });

  // Company state
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyData, setCompanyData] = useState({
    companyName: user?.companyName || "",
    country: user?.country || "",
    bankName: user?.bankName || "",
    ibanNumber: user?.ibanNumber || "",
    swiftCode: user?.swiftCode || ""
  });
  
  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { fullName: string; phone: string }) => {
      const res = await apiRequest("PUT", "user/profile", data);
      // return await res.json();
      return res;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["user"], data);
      setIsEditingProfile(false);
      toast({
        title: "Profile updated",
        description: "Your profile information has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update profile",
        description: error.message,
        variant: "destructive",
      });
    }
  });
  
  // Company update mutation
  const updateCompanyMutation = useMutation({
    mutationFn: async (data: {
      companyName: string;
      country: string;
      bankName: string;
      ibanNumber: string;
      swiftCode: string;
    }) => {
      const res = await apiRequest("PUT", "user/company", data);
      // return await res.json();
      return res;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["user"], data);
      setIsEditingCompany(false);
      toast({
        title: "Company details updated",
        description: "Your company information has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update company details",
        description: error.message,
        variant: "destructive",
      });
    }
  });
  
  // Handle input changes
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompanyData((prev) => ({ ...prev, [name]: value }));
  };
  
  // Handle form submissions
  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };
  
  const handleCompanySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateCompanyMutation.mutate(companyData);
  };

  if (!user) {
    return (
      <Layout>
        <div className="container py-16 flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold mb-4">Please log in to view your settings</h1>
          <Button asChild>
            <Link href="/auth">Log In</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  // Choose layout based on user role
  let RoleLayout: React.ComponentType<{ children: React.ReactNode }> = Layout;
  if (user.role === UserRole.ADMIN) RoleLayout = AdminLayout;
  else if (user.role === UserRole.MANAGER) RoleLayout = ManagerLayout;
  else if (user.role === UserRole.OWNER) RoleLayout = OwnerLayout;
  else if (user.role === UserRole.AGENT) RoleLayout = AgentLayout;
  else if (user.role === UserRole.GUEST) RoleLayout = GuestLayout;

  return (
    <RoleLayout>
      <div className="container py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
                <CardDescription>Manage your account</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs
                  defaultValue={activeTab}
                  orientation="vertical"
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="flex flex-col h-auto items-stretch bg-transparent p-0 space-y-1">
                    <TabsTrigger
                      value="profile"
                      className="justify-start px-4 py-2 data-[state=active]:bg-muted"
                    >
                      <UserCircle className="h-4 w-4 mr-2" />
                      Profile
                    </TabsTrigger>

                    {user.role === UserRole.MANAGER && (
                      <TabsTrigger
                        value="company"
                        className="justify-start px-4 py-2 data-[state=active]:bg-muted"
                      >
                        <Building className="h-4 w-4 mr-2" />
                        Company Details
                      </TabsTrigger>
                    )}

                    {user.role === UserRole.ADMIN && (
                      <TabsTrigger
                        value="admin"
                        className="justify-start px-4 py-2 data-[state=active]:bg-muted"
                      >
                        <ShieldIcon className="h-4 w-4 mr-2" />
                        Admin Settings
                      </TabsTrigger>
                    )}
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="profile" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Profile</CardTitle>
                    <CardDescription>Manage your personal information</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isEditingProfile ? (
                      <form onSubmit={handleProfileSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="username">Username</Label>
                            <Input id="username" value={user.username} disabled className="mt-1 bg-muted/50" />
                            <p className="text-xs text-muted-foreground mt-1">Username cannot be changed</p>
                          </div>
                          <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" value={user.email} disabled className="mt-1 bg-muted/50" />
                            <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                          </div>
                          <div>
                            <Label htmlFor="fullName">Full Name</Label>
                            <Input 
                              id="fullName" 
                              name="fullName" 
                              value={profileData.fullName} 
                              onChange={handleProfileChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone">Phone</Label>
                            <Input 
                              id="phone" 
                              name="phone" 
                              value={profileData.phone} 
                              onChange={handleProfileChange}
                              className="mt-1" 
                              placeholder="+971 XX XXX XXXX"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsEditingProfile(false);
                              setProfileData({
                                fullName: user.fullName || "",
                                phone: user.phone || ""
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="submit"
                            disabled={updateProfileMutation.isPending}
                          >
                            {updateProfileMutation.isPending && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Save Changes
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-1 block">Username</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.username}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Email</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.email}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Full Name</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.fullName || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Phone</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.phone || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Account Type</label>
                            <p className="p-2 border rounded-md bg-muted/50 capitalize">{user.role}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Status</label>
                            <p className="p-2 border rounded-md bg-muted/50 capitalize">{user.status}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                  {!isEditingProfile && (
                    <CardFooter className="justify-end border-t px-6 py-4">
                      <Button onClick={() => setIsEditingProfile(true)}>
                        Edit Profile
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="company" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Company Details</CardTitle>
                    <CardDescription>Manage your company information</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isEditingCompany ? (
                      <form onSubmit={handleCompanySubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="companyName">Company Name</Label>
                            <Input 
                              id="companyName" 
                              name="companyName" 
                              value={companyData.companyName} 
                              onChange={handleCompanyChange}
                              className="mt-1"
                              placeholder="Your company name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="country">Country</Label>
                            <Input 
                              id="country" 
                              name="country" 
                              value={companyData.country} 
                              onChange={handleCompanyChange}
                              className="mt-1"
                              placeholder="e.g. United Arab Emirates"
                            />
                          </div>
                          <div>
                            <Label htmlFor="bankName">Bank Name</Label>
                            <Input 
                              id="bankName" 
                              name="bankName" 
                              value={companyData.bankName} 
                              onChange={handleCompanyChange}
                              className="mt-1"
                              placeholder="Your bank name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="ibanNumber">IBAN Number</Label>
                            <Input 
                              id="ibanNumber" 
                              name="ibanNumber" 
                              value={companyData.ibanNumber} 
                              onChange={handleCompanyChange}
                              className="mt-1"
                              placeholder="AE123456789012345678901"
                            />
                          </div>
                          <div>
                            <Label htmlFor="swiftCode">SWIFT/BIC Code</Label>
                            <Input 
                              id="swiftCode" 
                              name="swiftCode" 
                              value={companyData.swiftCode} 
                              onChange={handleCompanyChange}
                              className="mt-1"
                              placeholder="XXXXXXXX"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              International bank identification code
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsEditingCompany(false);
                              setCompanyData({
                                companyName: user.companyName || "",
                                country: user.country || "",
                                bankName: user.bankName || "",
                                ibanNumber: user.ibanNumber || "",
                                swiftCode: user.swiftCode || ""
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="submit"
                            disabled={updateCompanyMutation.isPending}
                          >
                            {updateCompanyMutation.isPending && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Save Changes
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-1 block">Company Name</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.companyName || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Country</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.country || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Bank Name</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.bankName || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">IBAN Number</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.ibanNumber || "Not set"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">SWIFT/BIC Code</label>
                            <p className="p-2 border rounded-md bg-muted/50">{user.swiftCode || "Not set"}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                  {!isEditingCompany && (
                    <CardFooter className="justify-end border-t px-6 py-4">
                      <Button onClick={() => setIsEditingCompany(true)}>
                        Edit Company Details
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="admin" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Admin Settings</CardTitle>
                    <CardDescription>Manage system-wide settings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p>Admin settings will be available soon.</p>
                    </div>
                  </CardContent>
                  <CardFooter className="justify-end border-t px-6 py-4">
                    <Button asChild>
                      <Link href="/admin/settings">Go to Admin Settings</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </RoleLayout>
  );
}